import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

// SEO Configuration - String-based SEO data
export const SEO_CONFIG = {
  homepage: {
    title:
      "EzSignly – Simple,Secure, online  Electronic Signatures for Businesses",
    keywords:
      "eSignatures, digital signature, electronic signature, business workflows, esign, esign online, esign documents, eSign Workflow, Sign Documents Online, eSign Integration, eSign api, eSign Solutions, eSign Anywhere, digital esign, esign free online",
    description:
      "Skip the printer. EzSignly lets you sign and send documents digitally in minutes—simple, secure, and legally binding. Open a free account today.",
  },
  pricing: {
    title: "EzSignly Pricing | Digital Signature & eSignature Plans",
    keywords:
      "electronic signature solutions, electronic signature, digitised signature, esignature pricing, digital signature pricing, esign price, esign pdf price, digital sign pdf pricing, esign free online, digital sign free, digital sign free online",
    description:
      "Choose the perfect EzSignly plan for your business. Flexible pricing options for individuals, teams, and enterprises.",
  },
  about: {
    title: "Everything to Know About eSignatures",
    keywords:
      "about e signature, about digital signature, about e siganture software online, about online signature free, know about e signature online, facts about digital signature, about online document signature software",
    description:
      "Everything to Know About eSignatures. Learn about EzSignly, digital signatures, and how electronic signatures can transform your document workflows.",
  },
  contact: {
    title: "Contact Us | ezSignly : Your Trusted eSignature Partner",
    keywords:
      "esignature services, digital signature software, esign tool contact, digital sign contact, Digital sign contact number, Digital sign contact free, e signature contact free",
    description:
      "Contact EzSignly - Your Trusted eSignature Partner. Get in touch with our team for support, questions, or to learn more about our digital signature solutions.",
  },
  login: {
    title: "Sign In to Your eSignature Account",
    keywords:
      "E signature login free, E signature online, Esign software free, E sign login portal, digital signature login, online document sign login, Digital signature login page free",
    description:
      "Sign in to your EzSignly eSignature account. Access your digital signature dashboard and manage your documents securely.",
  },
  signup: {
    title: "Sign PDFs with Electronic Signatures",
    keywords:
      "Digital signature software free, E signature online, eSignature platforms, online e signature signup, digital signature signup, create e sign account, free signup e signature tool, online signature software signup for free",
    description:
      "Sign PDFs with Electronic Signatures. Create your free EzSignly account and start signing documents digitally today.",
  },
  "real-estate": {
    title: "Real Estate eSignature Software | Secure Digital Signatures",
    keywords:
      "digital signature, electronic signature, eSign Contracts, sign documents online, digital esign, digitized esign for realtors, digital sign for realestate, esign for realestate, esign free online",
    description:
      "Streamline real estate transactions with EzSignly's secure eSignature software. Sign contracts, agreements, and closing documents digitally from anywhere.",
  },
  hr: {
    title: "eSignature for HR: Faster Hiring & Smarter Workflows",
    keywords:
      "e-signature it solutions, esign for HR, esign free online, digital sign free, digital sign free online, esign, esign online, esign documents, digital esign free, esign for HR Solutions, digital sign for HR",
    description:
      "Streamline HR workflows with EzSignly's eSignature solution. Speed up hiring, onboarding, and document processing with secure digital signatures for HR teams.",
  },
  legal: {
    title: "Best eSignatures for Legal Teams | Law Firms",
    keywords:
      "esign documents, digital esign legal doc, legal e-signature solution, digital signature for legal documents, esign legal documents, eSign Contracts, sign documents online, Digitize document workflows, digital sign for affidavits, digital sign settlement agreements, e sign for court filings, electronic signature solutions, esign, esign online",
    description:
      "Compliant eSignatures for Legal Departments. Streamline legal document workflows with EzSignly's secure eSignature solution for law firms and legal teams.",
  },
  "small-business": {
    title: "Best esignature software for small business",
    keywords:
      "esign documents, esignature for business, digital signature for business, digital esign legal doc, sign documents online, Digitize document workflows, electronic signature for business, electronic signature solutions, digitised signature, esign, esign online, digital signature",
    description:
      "Best eSignature software for small businesses. Streamline agreements and paperwork with EzSignly's secure digital signature solution designed for small business owners.",
  },
  tutorial: {
    title: "Beginners guide to electronic signatures",
    keywords:
      "esignature tutorial, digital sign tutorial, esign tutorial, paperless esign tutorial, Esign tutorial pdf, electronic signature, electronic signature solutions, paperless signature, digitised signature, esign guide, Free eSign document, digitised signature, esign, esign online, digital signature",
    description:
      "Beginners guide to electronic signatures. Learn how to use EzSignly with our comprehensive tutorial on digital signatures and paperless document signing.",
  },
  blogs: {
    title: "esignature - Blog",
    keywords:
      "electronic signature services, online document signature, e sign online, document e signature, digital sign online, e signature services, online e sign, esign guide, Free eSign document, digitised signature, esign, esign online, digital signature, e sign blog, esign article, digital signature article, digital signature blog",
    description:
      "Explore expert insights, how-to guides, and articles on eSignatures, digital signatures, and electronic document signing. Learn about best practices, tips, and industry trends.",
  },
  // Add more indexes as needed
};

// Default SEO values
const DEFAULT_SEO = {
  title: "Fast and Easy Electronic Signature Solutions For Modern Business",
  keywords: "",
  description: "",
};

/**
 * SEO Component - Manages page SEO meta tags using react-helmet-async
 * @param {string|object} props - Either an index (string) to look up in SEO_CONFIG, or an object with title, keywords, description
 * @param {string} props.index - Index (string) to look up in SEO_CONFIG (e.g., "homepage", "pricing", "about")
 * @param {string} props.title - Page title (optional if using index)
 * @param {string} props.keywords - Meta keywords (optional if using index)
 * @param {string} props.description - Meta description (optional if using index)
 */
const SEO = ({ index, title, keywords, description }) => {
  const location = useLocation();

  // Calculate SEO data - recalculates when props change
  const seoData = useMemo(() => {
    if (index && SEO_CONFIG[index]) {
      // Use index-based lookup
      return SEO_CONFIG[index];
    } else if (title || keywords || description) {
      // Use direct props (override with provided values)
      return {
        title: title || DEFAULT_SEO.title,
        keywords: keywords || DEFAULT_SEO.keywords,
        description: description || DEFAULT_SEO.description,
      };
    }
    return DEFAULT_SEO;
  }, [index, title, keywords, description]);

  // Use location.pathname as key to force Helmet to update on route changes
  const helmetKey = `${location.pathname}-${index || title || "default"}`;

  return (
    <Helmet key={helmetKey}>
      {seoData.title && <title>{seoData.title}</title>}
      {seoData.keywords && <meta name="keywords" content={seoData.keywords} />}
      {seoData.description && (
        <meta name="description" content={seoData.description} />
      )}
    </Helmet>
  );
};

export default SEO;
